/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowRight, Shield, Zap, Layers, Store, CreditCard, Code2 } from 'lucide-react';
import { PUBLIC_NAV } from '@/constants/testIds';

export default function Landing() {
  return (
    <div className="min-h-screen bg-white fade-in">
      {/* Nav */}
      <nav className="border-b border-zinc-200 bg-white/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid={PUBLIC_NAV.logoLink} className="flex items-center gap-2">
            <div className="w-8 h-8 bg-zinc-950 flex items-center justify-center">
              <span className="text-white font-bold text-sm font-mono">EC</span>
            </div>
            <span className="font-display text-lg font-bold tracking-tight">External Checkout</span>
          </Link>
          <div className="flex items-center gap-4">
            <a href="#features" className="text-sm text-zinc-600 hover:text-zinc-950 transition">Fonctionnalités</a>
            <a href="#how" className="text-sm text-zinc-600 hover:text-zinc-950 transition">Comment ça marche</a>
            <Link to="/admin/login" data-testid={PUBLIC_NAV.loginLink}>
              <Button variant="outline" size="sm" className="rounded-md">Admin</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-6">— Checkout externe pour Shopify</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tighter">
              Encaissez sur Stripe.<br />
              <span className="text-zinc-500">Vendez sur Shopify.</span>
            </h1>
            <p className="mt-8 text-lg text-zinc-600 max-w-xl leading-relaxed">
              Un checkout externe haut de gamme qui transforme tout site vitrine ou boutique Shopify
              en machine à conversion. Les paiements partent sur votre Stripe, les commandes atterrissent
              automatiquement dans Shopify.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/admin/login" data-testid={PUBLIC_NAV.heroCta}>
                <Button className="btn-primary rounded-md px-6 h-12 text-base">
                  Accéder au tableau de bord <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#how">
                <Button variant="outline" className="rounded-md px-6 h-12 text-base">
                  Voir le fonctionnement
                </Button>
              </a>
            </div>

            <div className="mt-12 flex items-center gap-8 text-sm text-zinc-500">
              <div className="flex items-center gap-2"><Shield className="h-4 w-4" /> Stripe PCI</div>
              <div className="flex items-center gap-2"><Zap className="h-4 w-4" /> Multi-boutiques</div>
              <div className="flex items-center gap-2"><Layers className="h-4 w-4" /> Embed ready</div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <Card className="card-flat p-6 rotate-1 hover:rotate-0 transition-transform">
                <div className="overline mb-3">Aperçu</div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
                    <div>
                      <div className="font-medium">Sneakers édition limitée</div>
                      <div className="text-xs text-zinc-500 font-mono">SKU: SNK-2026-RED</div>
                    </div>
                    <div className="font-mono font-bold">€149,00</div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Sous-total</span>
                    <span className="font-mono">€149,00</span>
                  </div>
                  <div className="flex items-center justify-between text-lg font-bold">
                    <span>Total</span>
                    <span className="font-mono">€149,00</span>
                  </div>
                  <Button className="btn-accent w-full rounded-md h-12 mt-2">
                    <CreditCard className="h-4 w-4 mr-2" /> Payer avec Stripe
                  </Button>
                </div>
              </Card>
              <div className="absolute -bottom-6 -left-6 -z-10 w-full h-full border border-zinc-200 -rotate-1" />
            </div>
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="overline mb-4">— Fonctionnalités</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            Tout ce qu'il faut pour vendre, encaisser et tracer.
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-6 gap-4">
            <Card className="card-flat p-8 md:col-span-4">
              <Store className="h-7 w-7 mb-4" />
              <h3 className="font-display text-xl font-bold mb-2">Multi-boutiques Shopify</h3>
              <p className="text-zinc-600 leading-relaxed">
                Gérez plusieurs stores Shopify depuis un seul tableau de bord. Chaque commande payée
                est automatiquement créée dans la bonne boutique via l'Admin API.
              </p>
            </Card>
            <Card className="card-flat p-8 md:col-span-2">
              <CreditCard className="h-7 w-7 mb-4" />
              <h3 className="font-display text-xl font-bold mb-2">Stripe natif</h3>
              <p className="text-zinc-600">
                Les paiements arrivent directement sur votre compte Stripe.
              </p>
            </Card>
            <Card className="card-flat p-8 md:col-span-2">
              <Code2 className="h-7 w-7 mb-4" />
              <h3 className="font-display text-xl font-bold mb-2">Snippet embeddable</h3>
              <p className="text-zinc-600">
                Un bout de code à coller. Bouton "Payer" prêt à l'emploi sur tout site.
              </p>
            </Card>
            <Card className="card-flat p-8 md:col-span-4">
              <Shield className="h-7 w-7 mb-4" />
              <h3 className="font-display text-xl font-bold mb-2">Sécurité by design</h3>
              <p className="text-zinc-600 leading-relaxed">
                Montants verrouillés côté serveur. Auth admin via JWT. Aucune carte bancaire ne transite
                par votre infrastructure — tout est délégué à Stripe (PCI DSS Level 1).
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="overline mb-4">— Le flux</div>
          <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
            Trois étapes. Aucun friction.
          </h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { n: '01', t: 'Le client clique', d: 'Sur votre site vitrine ou directement depuis Shopify, le client clique sur le bouton "Payer".' },
              { n: '02', t: 'Paiement Stripe', d: 'Il est redirigé vers une page de checkout sécurisée. Stripe encaisse pour vous.' },
              { n: '03', t: 'Commande Shopify', d: 'La commande est créée automatiquement dans la bonne boutique Shopify, marquée comme payée.' },
            ].map((s) => (
              <Card key={s.n} className="card-flat p-8">
                <div className="font-mono text-xs text-zinc-400 mb-4">{s.n}</div>
                <h3 className="font-display text-xl font-bold mb-2">{s.t}</h3>
                <p className="text-zinc-600 leading-relaxed">{s.d}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-sm text-zinc-500 font-mono">© 2026 External Checkout</div>
          <div className="text-sm text-zinc-500">Built with Stripe + Shopify Admin API</div>
        </div>
      </footer>
    </div>
  );
}
